import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

/**
 * Real puzzle interaction:
 *  - pieces start scattered + rotated (the image is broken)
 *  - drag a piece, drop NEAR its home slot → it snaps in and LOCKS
 *  - drop far from home → it stays where you dropped it (keep working)
 *  - faint dashed slot outlines show empty homes
 *  - all 18 locked → the ghost fades, the panorama lights up
 */
const PANORAMA =
  "https://readdy.ai/api/search-image?query=Continuous%20vertical%20natural%20panorama%20combining%20snow%20capped%20alpine%20mountains%20at%20the%20top%2C%20deep%20green%20primeval%20pine%20forest%20in%20the%20middle%20layer%2C%20vast%20lush%20alpine%20meadow%20with%20wildflowers%20and%20a%20winding%20clear%20river%20in%20the%20foreground%2C%20two%20small%20travelers%20riding%20horses%20gently%20across%20the%20grassland%2C%20cinematic%20soft%20morning%20daylight%2C%20fresh%20healing%20atmosphere%2C%20editorial%20travel%20photography%2C%20clean%20unified%20cool%20green%20and%20snow%20gray%20palette%2C%20painterly%20realism%2C%20high%20resolution%20natural%20texture&width=1400&height=1750&seq=puzzle-pano-real-v3&orientation=portrait";

type Tile = {
  id: number;
  x: number; // % home
  y: number;
  w: number;
  h: number;
};

// 5 large editorial tiles — composed asymmetrically.
// Row 1: one wide panoramic top (sky + peaks).
// Row 2: two side-by-side forest panels.
// Row 3: two foreground meadow panels (asymmetric widths).
const TILES: Tile[] = [
  { id: 1, x: 0, y: 0, w: 100, h: 30 },
  { id: 2, x: 0, y: 30, w: 50, h: 35 },
  { id: 3, x: 50, y: 30, w: 50, h: 35 },
  { id: 4, x: 0, y: 65, w: 55, h: 35 },
  { id: 5, x: 55, y: 65, w: 45, h: 35 },
];

type Offset = { dx: number; dy: number; rot: number };

const SNAP_DISTANCE = 60; // px from home → snap & lock

// Deterministic, restrained scatter — pieces drift just a touch from home,
// each at a different angle. Editorial, not chaotic.
const INITIAL_SCATTER: Record<number, Offset> = {
  1: { dx: -28, dy: -22, rot: -3.5 },
  2: { dx: -36, dy: 18, rot: -2.5 },
  3: { dx: 32, dy: -14, rot: 3 },
  4: { dx: -22, dy: 30, rot: -2 },
  5: { dx: 30, dy: 26, rot: 3.5 },
};

function getInitial(id: number): Offset {
  return INITIAL_SCATTER[id] ?? { dx: 0, dy: 0, rot: 0 };
}

function buildInitialOffsets(): Record<number, Offset> {
  const o: Record<number, Offset> = {};
  TILES.forEach((t) => {
    o[t.id] = getInitial(t.id);
  });
  return o;
}

export default function PuzzleHero() {
  const [offsets, setOffsets] = useState<Record<number, Offset>>(() =>
    buildInitialOffsets()
  );
  const [locked, setLocked] = useState<Set<number>>(new Set());
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [animatingIds, setAnimatingIds] = useState<Set<number>>(new Set());
  const [justSnappedId, setJustSnappedId] = useState<number | null>(null);
  const [entered, setEntered] = useState(false);

  const dragRef = useRef<{
    id: number;
    startX: number;
    startY: number;
    startDx: number;
    startDy: number;
    startRot: number;
  } | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setEntered(true), 200);
    return () => window.clearTimeout(t);
  }, []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>, id: number) => {
      if (locked.has(id)) return; // locked pieces are fixed
      e.preventDefault();
      const cur = offsets[id] ?? getInitial(id);
      dragRef.current = {
        id,
        startX: e.clientX,
        startY: e.clientY,
        startDx: cur.dx,
        startDy: cur.dy,
        startRot: cur.rot,
      };
      setDraggingId(id);
      setAnimatingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [locked, offsets]
  );

  useEffect(() => {
    function move(e: PointerEvent) {
      if (!dragRef.current) return;
      const d = dragRef.current;
      const dx = d.startDx + (e.clientX - d.startX);
      const dy = d.startDy + (e.clientY - d.startY);
      // As the piece travels, gently un-rotate it toward 0 based on how
      // close it is getting to home — feels like the piece "knows" where to go.
      const dist = Math.hypot(dx, dy);
      const t = Math.min(1, dist / 220);
      const rot = d.startRot * t;
      setOffsets((prev) => ({ ...prev, [d.id]: { dx, dy, rot } }));
    }
    function up() {
      if (!dragRef.current) return;
      const id = dragRef.current.id;
      dragRef.current = null;
      setDraggingId(null);

      setOffsets((prev) => {
        const cur = prev[id] ?? getInitial(id);
        const dist = Math.hypot(cur.dx, cur.dy);
        if (dist <= SNAP_DISTANCE) {
          // SNAP HOME + LOCK
          setAnimatingIds((p) => new Set(p).add(id));
          setLocked((p) => new Set(p).add(id));
          setJustSnappedId(id);
          window.setTimeout(() => {
            setJustSnappedId((cur2) => (cur2 === id ? null : cur2));
          }, 700);
          return { ...prev, [id]: { dx: 0, dy: 0, rot: 0 } };
        }
        // stay where dropped
        return prev;
      });
    }
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, []);

  const piecesHome = locked.size;
  const isComplete = piecesHome === TILES.length;

  // Hint: which tile is closest to home (for the small "nearby" arrow on slot)
  const dragCloseToHome = useMemo(() => {
    if (draggingId == null) return false;
    const o = offsets[draggingId];
    if (!o) return false;
    return Math.hypot(o.dx, o.dy) <= SNAP_DISTANCE;
  }, [draggingId, offsets]);

  return (
    <section className="relative w-full min-h-screen overflow-hidden bg-white">
      <div className="relative mx-auto flex flex-col lg:flex-row w-full max-w-[1480px] px-8 lg:px-14 pt-32 pb-16 gap-10 lg:gap-0">
        {/* ---------------- LEFT: TEXT ---------------- */}
        <div className="relative w-full lg:w-[44%] flex flex-col justify-between min-h-[78vh]">
          {/* ===== Decorative landscape marks — restrained & editorial ===== */}
          {/* The mountain range — the single hero decoration.
              Sits behind the title baseline as a quiet horizon. */}
          <div className="hidden lg:flex absolute top-[40%] right-[6%] items-end gap-[3px] pointer-events-none deco-float-slow" style={{ animationDelay: "-2s" }}>
            {/* faint back ridge */}
            <span
              className="block"
              style={{
                width: 0,
                height: 0,
                borderLeft: "34px solid transparent",
                borderRight: "34px solid transparent",
                borderBottom: "52px solid rgba(74,107,81,0.14)",
                marginRight: -10,
                marginBottom: 4,
              }}
            />
            {/* main peak */}
            <span
              className="block relative"
              style={{
                width: 0,
                height: 0,
                borderLeft: "54px solid transparent",
                borderRight: "54px solid transparent",
                borderBottom: "86px solid rgba(31,58,37,0.22)",
              }}
            >
              {/* snow cap stroke on the main peak */}
              <span
                className="absolute"
                style={{
                  left: -16,
                  top: 18,
                  width: 32,
                  height: 1,
                  background: "rgba(255,255,255,0.55)",
                  transform: "rotate(-30deg)",
                  transformOrigin: "left center",
                }}
              />
            </span>
            {/* secondary peak */}
            <span
              className="block"
              style={{
                width: 0,
                height: 0,
                borderLeft: "32px solid transparent",
                borderRight: "32px solid transparent",
                borderBottom: "56px solid rgba(74,107,81,0.18)",
                marginLeft: -14,
              }}
            />
            {/* tiny far ridge */}
            <span
              className="block"
              style={{
                width: 0,
                height: 0,
                borderLeft: "18px solid transparent",
                borderRight: "18px solid transparent",
                borderBottom: "28px solid rgba(74,107,81,0.12)",
                marginLeft: -6,
                marginBottom: 2,
              }}
            />
          </div>

          {/* A single thin horizon line, anchoring the mountains in space */}
          <span className="hidden lg:block absolute top-[64%] right-[4%] w-[260px] h-px bg-gradient-to-r from-transparent via-[#4a6b51]/30 to-transparent pointer-events-none" />

          {/* One quiet editorial mark — small diamond + line, top-right */}
          <div className="hidden lg:flex absolute top-[8%] right-4 flex-col items-center gap-2 pointer-events-none">
            <span className="w-1.5 h-1.5 rotate-45 border border-[#4a6b51]/45" />
            <span className="h-14 w-px bg-gradient-to-b from-[#4a6b51]/35 to-transparent" />
          </div>
          {/* ===== /Decorative marks ===== */}

          {/* Top epigraph */}
          <div className="max-w-[360px]">
            <div className="flex items-center gap-3 text-deepgreen-muted">
              <span className="h-px w-8 bg-[#4a6b51]/50" />
              <span className="font-serif text-[10px] tracking-[0.45em] uppercase">
                An Epigraph
              </span>
            </div>
            <p className="mt-5 font-serif italic text-[17px] md:text-[18px] text-deepgreen leading-[1.7] tracking-[0.005em]">
              The wild does not ask who you are —
              <br />
              it lets the grasses bow,
              <br />
              and the song rise wide as the field.
            </p>
          </div>

          {/* Elegant display title */}
          <div className="relative mt-12 lg:mt-0">
            <h1 className="font-display text-deepgreen leading-[0.92] tracking-[0.01em] text-[80px] md:text-[110px] lg:text-[136px] font-normal">
              <span className="block">Wild</span>
              <span className="block">Horizons</span>
            </h1>
            <p className="mt-5 font-serif italic text-[14px] md:text-[15px] tracking-[0.45em] uppercase text-deepgreen-muted font-light">
              Boundless&nbsp;·&nbsp;Wilderness&nbsp;·&nbsp;Travel
            </p>
          </div>

          {/* Bottom healing slogan */}
          <div className="mt-12 lg:mt-0">
            <p className="font-serif italic text-[20px] md:text-[22px] text-deepgreen leading-snug">
              However bumpy the road of life may be —
            </p>
            <p className="font-serif text-[12px] md:text-[13px] tracking-[0.5em] uppercase text-deepgreen-muted mt-3">
              we are still meant to live it brilliantly
            </p>
          </div>
        </div>

        {/* ---------------- RIGHT: PUZZLE PANORAMA ---------------- */}
        <div className="relative w-full lg:w-[56%] lg:pl-6">
          {/* Puzzle container — locked to a 4:5 portrait box matching the panorama */}
          <div
            className={`puzzle-group relative w-full max-w-[640px] mx-auto transition-opacity duration-700 ${
              entered ? "opacity-100" : "opacity-0"
            }`}
            style={{ aspectRatio: "4 / 5" }}
            aria-label="Interactive wilderness puzzle panorama"
          >
            {/* Ghost panorama — fades out as you complete */}
            <img
              src={PANORAMA}
              alt="Complete wilderness panorama"
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover object-top rounded-sm select-none pointer-events-none transition-all duration-1000"
              style={{
                opacity: isComplete ? 0 : 0.14,
                filter: "saturate(0.8)",
              }}
            />
            {/* Frame */}
            <div
              className={`absolute inset-0 rounded-sm pointer-events-none transition-all duration-700 ${
                isComplete
                  ? "ring-2 ring-[#1f3a25]/30"
                  : "ring-1 ring-stone-200"
              }`}
            />

            {/* SLOT GUIDES — show where each unlocked piece belongs */}
            {TILES.map((t) => {
              if (locked.has(t.id)) return null;
              const isTarget = draggingId === t.id;
              return (
                <div
                  key={`slot-${t.id}`}
                  className="absolute pointer-events-none"
                  style={{
                    left: `${t.x}%`,
                    top: `${t.y}%`,
                    width: `${t.w}%`,
                    height: `${t.h}%`,
                    padding: "3px",
                  }}
                >
                  <div
                    className={`w-full h-full rounded-[3px] transition-all duration-300 ${
                      isTarget
                        ? dragCloseToHome
                          ? "ring-2 ring-[#1f3a25] bg-[#1f3a25]/8"
                          : "ring-2 ring-[#4a6b51]/60 bg-[#4a6b51]/5"
                        : "ring-1 ring-dashed ring-[#4a6b51]/25"
                    }`}
                    style={{
                      borderStyle: isTarget ? "solid" : "dashed",
                    }}
                  />
                </div>
              );
            })}

            {/* PUZZLE TILES */}
            {TILES.map((t) => {
              const bgSizeX = (100 / t.w) * 100;
              const bgSizeY = (100 / t.h) * 100;
              const bgPosX = t.w >= 100 ? 50 : (t.x / (100 - t.w)) * 100;
              const bgPosY = t.h >= 100 ? 50 : (t.y / (100 - t.h)) * 100;

              const off = offsets[t.id] ?? { dx: 0, dy: 0, rot: 0 };
              const isDragging = draggingId === t.id;
              const isLocked = locked.has(t.id);
              const isSnapping = justSnappedId === t.id;
              const shouldAnimate = animatingIds.has(t.id) && !isDragging;

              const shouldWobble = !isLocked && !isDragging && !isComplete;

              return (
                <div
                  key={t.id}
                  className="puzzle-tile-wrap absolute"
                  style={{
                    left: `${t.x}%`,
                    top: `${t.y}%`,
                    width: `${t.w}%`,
                    height: `${t.h}%`,
                    // Pad pieces apart when not yet whole; collapse to 0 to
                    // form a seamless image once everything is locked.
                    padding: isComplete ? "0px" : "3px",
                    transition: "padding 700ms cubic-bezier(0.22, 1, 0.36, 1)",
                    zIndex: isDragging
                      ? 50
                      : isSnapping
                      ? 40
                      : isLocked
                      ? 15
                      : 25,
                  }}
                >
                  <div
                    role="button"
                    tabIndex={isLocked ? -1 : 0}
                    aria-label={
                      isLocked
                        ? `Piece ${t.id} placed`
                        : `Drag puzzle piece ${t.id} to its slot`
                    }
                    onPointerDown={(e) => onPointerDown(e, t.id)}
                    className={`puzzle-tile-inner w-full h-full relative ${
                      isLocked
                        ? "cursor-default is-locked"
                        : isDragging
                        ? "is-dragging cursor-grabbing"
                        : "cursor-grab"
                    } ${isSnapping ? "is-snapping" : ""} ${
                      shouldWobble ? "is-wobbling" : ""
                    }`}
                    style={
                      {
                        transform: `translate(${off.dx}px, ${off.dy}px) rotate(${off.rot}deg)`,
                        transition: shouldAnimate
                          ? "transform 520ms cubic-bezier(0.34, 1.56, 0.64, 1)"
                          : "none",
                        touchAction: "none",
                        // per-tile wobble timing so they don't all shake in sync
                        "--wob-delay": `${(t.id % 5) * 0.7}s`,
                        "--wob-dur": `${3.4 + (t.id % 3) * 0.5}s`,
                      } as React.CSSProperties
                    }
                  >
                    <div
                      className={`w-full h-full select-none overflow-hidden ${
                        isComplete ? "rounded-none" : "rounded-[2px]"
                      }`}
                      style={{
                        backgroundImage: `url(${PANORAMA})`,
                        backgroundSize: `${bgSizeX}% ${bgSizeY}%`,
                        backgroundPosition: `${bgPosX}% ${bgPosY}%`,
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Completion shimmer */}
            {isComplete && (
              <div className="absolute inset-0 pointer-events-none rounded-sm overflow-hidden">
                <div className="complete-shimmer absolute inset-0" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2 rounded-full bg-[#1f3a25] text-white font-serif text-[11px] tracking-[0.35em] uppercase complete-badge">
                  ✦ The Horizon is Whole ✦
                </div>
              </div>
            )}
          </div>

          {/* Scroll cue */}
          <div className="hidden lg:flex absolute -bottom-2 right-2 items-center gap-3 text-deepgreen-muted">
            <span className="font-serif text-[10px] tracking-[0.4em] uppercase">
              Scroll&nbsp;to&nbsp;explore
            </span>
            <span className="h-8 w-px bg-[#4a6b51]/40" />
          </div>
        </div>
      </div>

      {/* scoped styles */}
      <style>{`
        .puzzle-tile-inner {
          will-change: transform;
        }
        .puzzle-tile-inner.is-dragging > div {
          box-shadow: 0 16px 38px -12px rgba(20, 50, 28, 0.55);
          outline: 2px solid rgba(255, 255, 255, 0.95);
          outline-offset: -2px;
        }
        .puzzle-tile-inner.is-locked > div {
          outline: 1px solid rgba(31, 58, 37, 0.18);
          outline-offset: -1px;
        }
        .puzzle-tile-inner.is-snapping > div {
          animation: snap-pulse 700ms cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes snap-pulse {
          0% { box-shadow: 0 0 0 0 rgba(31, 58, 37, 0.55); outline: 2px solid rgba(255,255,255,1); outline-offset: -2px; }
          60% { box-shadow: 0 0 0 14px rgba(31, 58, 37, 0); }
          100% { box-shadow: 0 0 0 0 rgba(31, 58, 37, 0); }
        }

        /* Intermittent, loose wobble for unrestored pieces — the base
           transform (drift + rotation) lives on the wrapper, the wobble
           overlays a tiny rotation/translate on the inner image only. */
        .puzzle-tile-inner.is-wobbling > div {
          animation: tile-wobble var(--wob-dur, 3.6s) ease-in-out var(--wob-delay, 0s) infinite;
          transform-origin: 50% 50%;
        }
        @keyframes tile-wobble {
          0%, 100%   { transform: translate(0, 0) rotate(0deg); }
          6%         { transform: translate(-0.6px, 0.4px) rotate(-0.6deg); }
          12%        { transform: translate(0.7px, -0.3px) rotate(0.5deg); }
          18%        { transform: translate(-0.5px, 0.6px) rotate(-0.4deg); }
          24%        { transform: translate(0.3px, -0.5px) rotate(0.3deg); }
          30%, 100%  { transform: translate(0, 0) rotate(0deg); }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .complete-shimmer {
          background: linear-gradient(
            110deg,
            transparent 35%,
            rgba(255,255,255,0.55) 50%,
            transparent 65%
          );
          animation: shimmer 1500ms ease-out forwards;
        }

        @keyframes badge-in {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.85); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        .complete-badge {
          animation: badge-in 700ms cubic-bezier(0.34, 1.56, 0.64, 1) 400ms both;
        }

        /* Slow ambient drift for the decorative landscape marks */
        .deco-float-slow {
          animation: deco-float-slow 12s ease-in-out infinite;
        }
        @keyframes deco-float-slow {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(0, -4px); }
        }
      `}</style>
    </section>
  );
}